/**
 * One notice in the toast stack.
 *
 * Presentational only — the queue, the timers and the focus handling live in
 * ToastContext. Styled after the existing workflow/export banners rather than
 * a new toast library.
 */

import { X } from "lucide-react";
import React from "react";

export type ToastVariant = "success" | "error" | "info";

export interface ToastMessage {
	id: number;
	text: string;
	variant: ToastVariant;
}

/**
 * Why a notice is being held open. Hover and focus are tracked separately
 * because they overlap: a pointer leaving a card the keyboard is still inside
 * must not restart the clock underneath the person reading it.
 */
export type ToastPauseReason = "hover" | "focus";

interface ToastProps {
	toast: ToastMessage;
	onDismiss: (id: number) => void;
	/** Hover/focus holds the notice open — a 3s timer is a reading deadline. */
	onPause: (id: number, reason: ToastPauseReason) => void;
	onResume: (id: number, reason: ToastPauseReason) => void;
}

const TOAST_STYLES: Record<ToastVariant, string> = {
	success: "border-emerald-200 bg-emerald-50 text-emerald-800",
	error: "border-red-200 bg-red-50 text-red-800",
	info: "border-gray-200 bg-white text-text-base",
};

const Toast: React.FC<ToastProps> = ({
	toast,
	onDismiss,
	onPause,
	onResume,
}) => (
	<div
		// An error replaced a blocking alert(), so it interrupts; the rest wait
		// their turn. The politeness lives here, on the message — never on a
		// wrapper that would have to be polite and assertive at once.
		role={toast.variant === "error" ? "alert" : "status"}
		onMouseEnter={() => onPause(toast.id, "hover")}
		onMouseLeave={() => onResume(toast.id, "hover")}
		onFocus={() => onPause(toast.id, "focus")}
		onBlur={() => onResume(toast.id, "focus")}
		className={`pointer-events-auto flex w-80 max-w-[calc(100vw-2rem)] items-start gap-2 rounded-lg border px-3 py-2 text-sm shadow-lg ${TOAST_STYLES[toast.variant]}`}
	>
		<span className="flex-1">{toast.text}</span>
		<button
			type="button"
			aria-label="Dismiss notification"
			onClick={() => onDismiss(toast.id)}
			className="opacity-70 hover:opacity-100"
		>
			<X className="w-4 h-4" />
		</button>
	</div>
);

export default Toast;
